import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { fetchCountries, checkEmailExists, fetchAllContacts, pb, generateContactPassword } from "../../api/pocketbase";
import { fetchCompanies as refreshCompaniesList } from "../../features/CompanySlice";
import CountryDropdown from "../common/CountryDropdown";
import ContactSearchDropdown from "../common/ContactSearchDropdown";

const CreateCustomer = ({ onCreated }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreateArmed, setIsCreateArmed] = useState(false);
  const [totalSteps] = useState(4);
  const modalRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [emailErrors, setEmailErrors] = useState({
    SuperAdminEmail: "",
    ARCChiefEmail: "",
    MainAdminEmail: ""
  });
  const [isEmailValidating, setIsEmailValidating] = useState({
    SuperAdminEmail: false,
    ARCChiefEmail: false,
    MainAdminEmail: false
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [createdContactIds, setCreatedContactIds] = useState({
    SuperAdmin: null,
    ARCChief: null,
    MainAdmin: null
  });
  
  // New state for existing contact selection
  const [useExistingContact, setUseExistingContact] = useState({
    SuperAdmin: false,
    ARCChief: false,
    MainAdmin: false
  });
  const [selectedContactId, setSelectedContactId] = useState({
    SuperAdmin: null,
    ARCChief: null,
    MainAdmin: null
  });

  const loadContacts = async () => {
    try {
      const contacts = await fetchAllContacts();
      setAllContacts(contacts);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryList = await fetchCountries();
        setCountries(countryList);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    loadCountries();
    loadContacts();
  }, []);

  useEffect(() => {
    if (!isOpen || currentStep !== 4) {
      setIsCreateArmed(false);
      return;
    }
    const timer = setTimeout(() => setIsCreateArmed(true), 400);
    return () => clearTimeout(timer);
  }, [isOpen, currentStep]);

  // Email validation function (PocketBase duplicate check)
  const validateEmail = async (email, fieldName) => {
    const trimmedEmail = (email ?? "").trim();
    const isRequiredField = fieldName === "SuperAdminEmail";

    setIsEmailValidating((prev) => ({ ...prev, [fieldName]: true }));

    try {
      if (!trimmedEmail) {
        setEmailErrors((prev) => ({
          ...prev,
          [fieldName]: isRequiredField ? "Email is required" : "",
        }));
        return !isRequiredField;
      }

      const emailExists = await checkEmailExists(trimmedEmail);

      if (emailExists) {
        setEmailErrors((prev) => ({
          ...prev,
          [fieldName]: "This email already exists in the system",
        }));
        return false;
      }

      setEmailErrors((prev) => ({ ...prev, [fieldName]: "" }));
      return true;
    } catch (error) {
      console.error("Email validation error:", error);
      setEmailErrors((prev) => ({
        ...prev,
        [fieldName]: "Error validating email. Please try again.",
      }));
      return false;
    } finally {
      setIsEmailValidating((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  // Create contact in Contacts collection
  const createContact = async (contactData, accessRight, companyId) => {
    try {
      const password = generateContactPassword();
      const contactRecord = await pb.collection('Contacts').create({
        Name: contactData.Name,
        Surname: contactData.Surname,
        email: contactData.email,
        MobileNumber: contactData.MobileNumber,
        emailVisibility: true,
        password,
        passwordConfirm: password,
        CanAccessPortal: true,
        AccessRight: accessRight,
        CompanyID: companyId ? [companyId] : []
      });
      return { id: contactRecord.id, email: contactData.email, password };
    } catch (error) {
      console.error(`Error creating ${accessRight} contact:`, error);
      throw error;
    }
  };

  // Validation Schema for each step
  const getStepValidationSchema = (step) => {
    switch(step) {
      case 1:
        return Yup.object().shape({
          SuperAdminName: useExistingContact.SuperAdmin 
            ? Yup.string() 
            : Yup.string().required("Super Admin Name is required"),
          SuperAdminSurname: useExistingContact.SuperAdmin 
            ? Yup.string() 
            : Yup.string().required("Super Admin Surname is required"),
          SuperAdminEmail: useExistingContact.SuperAdmin 
            ? Yup.string() 
            : Yup.string().email("Invalid email").required("Super Admin Email is required"),
          SuperAdminMobile: useExistingContact.SuperAdmin 
            ? Yup.string() 
            : Yup.string()
                .matches(/^\+?[0-9\s-]+$/, "Mobile number can only contain numbers, spaces, hyphens and + at the start")
                .required("Super Admin Mobile Number is required"),
        });
      case 2:
        return Yup.object().shape({
          ARCChiefName: Yup.string(),
          ARCChiefSurname: Yup.string(),
          ARCChiefEmail: Yup.string().typeError('Must be a string').nullable().matches(/^$|^.+@.+$/, "Invalid email"),
          ARCChiefMobile: Yup.string().typeError('Must be a string').nullable().matches(/^$|^\+?[0-9\s-]{8,15}$/, "Mobile number must be between 8-15 digits"),
        });
      case 3:
        return Yup.object().shape({
          MainAdminName: Yup.string(),
          MainAdminSurname: Yup.string(),
          MainAdminEmail: Yup.string().typeError('Must be a string').nullable().matches(/^$|^.+@.+$/, "Invalid email"),
          MainAdminMobile: Yup.string().typeError('Must be a string').nullable().matches(/^$|^\+?[0-9\s-]{8,15}$/, "Mobile number must be between 8-15 digits"),
        });
      case 4:
        return Yup.object().shape({
          CompanyName: Yup.string().required("Company Name is required"),
          CommercialName: Yup.string().required("Commercial Name is required"),
          Telephone1: Yup.string()
            .matches(/^\+?[0-9\s-]{8,15}$/, "Phone number must be between 8-15 digits and can contain spaces, hyphens and + at the start")
            .required("Phone is required"),
          InvoiceAddress: Yup.string().required("Invoice Address is required"),
          Country: Yup.string().required("Country is required"),
        });
      default:
        return Yup.object().shape({});
    }
  };

  const formik = useFormik({
    initialValues: {
      CompanyName: "",
      CommercialName: "",
      Telephone1: "",
      InvoiceAddress: "",
      Country: "",
      SuperAdminName: "",
      SuperAdminSurname: "",
      SuperAdminEmail: "",
      SuperAdminMobile: "",
      ARCChiefName: "",
      ARCChiefSurname: "",
      ARCChiefEmail: "",
      ARCChiefMobile: "",
      MainAdminName: "",
      MainAdminSurname: "",
      MainAdminEmail: "",
      MainAdminMobile: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const handleCreateCustomer = async () => {
      if (currentStep !== 4 || !isCreateArmed || isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      try {
        const values = formik.values;
        await getStepValidationSchema(4).validate(values, { abortEarly: false });

        const selectedCountry = countries.find((country) => country.name === values.Country);
        if (!selectedCountry?.id) {
          alert("Please select a valid country");
          setIsSubmitting(false);
          return;
        }

        const customerData = {
          CompanyName: values.CompanyName,
          CommercialName: values.CommercialName,
          InvoiceAddress: values.InvoiceAddress,
          Telephone1: values.Telephone1,
          Country: selectedCountry.id,
          Seats: 2,
          active: true
        };

        const newCustomer = await pb.collection('Customers').create(customerData);
        const createdPasswords = [];

        let superAdminId = useExistingContact.SuperAdmin ? selectedContactId.SuperAdmin : createdContactIds.SuperAdmin;
        let arcChiefId = useExistingContact.ARCChief ? selectedContactId.ARCChief : createdContactIds.ARCChief;
        let mainAdminId = useExistingContact.MainAdmin ? selectedContactId.MainAdmin : createdContactIds.MainAdmin;

        if (!superAdminId) {
          const created = await createContact({
            Name: values.SuperAdminName,
            Surname: values.SuperAdminSurname,
            email: values.SuperAdminEmail,
            MobileNumber: values.SuperAdminMobile
          }, "SuperUser", newCustomer.id);
          superAdminId = created.id;
          createdPasswords.push(created);
        } else {
          await pb.collection('Contacts').update(superAdminId, {
            "CompanyID+": newCustomer.id,
            SystemKnowledge: 1
          });
        }

        if (!arcChiefId && values.ARCChiefName && values.ARCChiefEmail) {
          const created = await createContact({
            Name: values.ARCChiefName,
            Surname: values.ARCChiefSurname,
            email: values.ARCChiefEmail,
            MobileNumber: values.ARCChiefMobile
          }, "Technical", newCustomer.id);
          arcChiefId = created.id;
          createdPasswords.push(created);
        } else if (arcChiefId) {
          await pb.collection('Contacts').update(arcChiefId, {
            "CompanyID+": newCustomer.id,
            SystemKnowledge: 1
          });
        }

        if (!mainAdminId && values.MainAdminName && values.MainAdminEmail) {
          const created = await createContact({
            Name: values.MainAdminName,
            Surname: values.MainAdminSurname,
            email: values.MainAdminEmail,
            MobileNumber: values.MainAdminMobile
          }, "Administrator", newCustomer.id);
          mainAdminId = created.id;
          createdPasswords.push(created);
        } else if (mainAdminId) {
          await pb.collection('Contacts').update(mainAdminId, {
            "CompanyID+": newCustomer.id,
            SystemKnowledge: 1
          });
        }

        await pb.collection('Customers').update(newCustomer.id, {
          SuperAdmin: superAdminId ? [superAdminId] : [],
          Administrator: mainAdminId ? [mainAdminId] : [],
          ARCChief: arcChiefId ? [arcChiefId] : []
        });

        if (createdPasswords.length > 0) {
          const passwordSummary = createdPasswords
            .map((item) => `${item.email}: ${item.password}`)
            .join("\n");
          alert(`Customer created. Share these one-time passwords:\n${passwordSummary}`);
        }
        
        formik.resetForm();
        setIsOpen(false);
        setCurrentStep(1);
        setEmailErrors({
          SuperAdminEmail: "",
          ARCChiefEmail: "",
          MainAdminEmail: ""
        });
        setIsSubmitDisabled(false);
        setIsSubmitting(false);
        setCreatedContactIds({
          SuperAdmin: null,
          ARCChief: null,
          MainAdmin: null
        });
        setUseExistingContact({
          SuperAdmin: false,
          ARCChief: false,
          MainAdmin: false
        });
        setSelectedContactId({
          SuperAdmin: null,
          ARCChief: null,
          MainAdmin: null
        });
        dispatch(refreshCompaniesList(""));
        if (onCreated) {
          onCreated();
        }
      } catch (error) {
        if (error?.inner) {
          const errors = {};
          error.inner.forEach((err) => {
            if (err.path) errors[err.path] = err.message;
          });
          formik.setErrors(errors);
          formik.setTouched({
            CompanyName: true,
            CommercialName: true,
            Telephone1: true,
            InvoiceAddress: true,
            Country: true,
          }, false);
        } else {
          alert(error?.message || "Error creating customer");
        }
        setIsSubmitting(false);
      }
  };

  // Optional early feedback on blur; full validation runs in handleNext
  const handleEmailBlur = async (e) => {
    if (isAdvancingStep) return;

    const email = e.target.value;
    const fieldName = e.target.name;

    if (!["SuperAdminEmail", "ARCChiefEmail", "MainAdminEmail"].includes(fieldName)) {
      return;
    }

    await validateEmail(email, fieldName);
  };

  // Handle next step
  const handleNext = async () => {
    if (isAdvancingStep) return;

    setIsAdvancingStep(true);

    try {
    console.log("Current Step:", currentStep);
    
    if (currentStep === 1) {
      console.log("Step 1 Values:", {
        name: formik.values.SuperAdminName,
        surname: formik.values.SuperAdminSurname,
        email: formik.values.SuperAdminEmail,
        mobile: formik.values.SuperAdminMobile,
        emailError: emailErrors.SuperAdminEmail,
        formikErrors: formik.errors,
        useExisting: useExistingContact.SuperAdmin,
        selectedId: selectedContactId.SuperAdmin
      });
    }

    // For step 1, validate that either new contact is created or existing is selected
    if (currentStep === 1) {
      if (useExistingContact.SuperAdmin) {
        if (!selectedContactId.SuperAdmin) {
          alert("Please select a contact from the dropdown");
          return;
        }
      } else {
        // Validate new contact fields
        if (!formik.values.SuperAdminName || !formik.values.SuperAdminSurname || 
            !formik.values.SuperAdminEmail || !formik.values.SuperAdminMobile) {
          alert("Please fill all required fields for Super Admin");
          return;
        }

        const isSuperAdminEmailValid = await validateEmail(
          formik.values.SuperAdminEmail,
          "SuperAdminEmail"
        );
        if (!isSuperAdminEmailValid) {
          return;
        }
      }
    }

    // Validate current step before proceeding
    const currentSchema = getStepValidationSchema(currentStep);
    const currentValues = {};
    
    // Get only the fields for current step
    Object.keys(currentSchema.fields).forEach(field => {
      currentValues[field] = formik.values[field];
    });

    // Validate form fields
    await currentSchema.validate(currentValues, { abortEarly: false });
      
      // For step 2 and 3, validate that either new contact is created or existing is selected
      if (currentStep === 2) {
        if (useExistingContact.ARCChief) {
          if (!selectedContactId.ARCChief) {
            alert("Please select a contact from the dropdown");
            return;
          }
        } else {
          const hasAnyField = formik.values.ARCChiefName || formik.values.ARCChiefSurname || 
                             formik.values.ARCChiefEmail || formik.values.ARCChiefMobile;
          if (hasAnyField) {
            // Check if all fields are filled when at least one is filled
            if (!formik.values.ARCChiefName || !formik.values.ARCChiefSurname || 
                !formik.values.ARCChiefEmail || !formik.values.ARCChiefMobile) {
              alert("Please fill all fields for ARC Chief or leave all empty");
              return;
            }

            const isArcChiefEmailValid = await validateEmail(
              formik.values.ARCChiefEmail,
              "ARCChiefEmail"
            );
            if (!isArcChiefEmailValid) {
              return;
            }
          }
        }
      }

      if (currentStep === 3) {
        if (useExistingContact.MainAdmin) {
          if (!selectedContactId.MainAdmin) {
            alert("Please select a contact from the dropdown");
            return;
          }
        } else {
          const hasAnyField = formik.values.MainAdminName || formik.values.MainAdminSurname || 
                             formik.values.MainAdminEmail || formik.values.MainAdminMobile;
          if (hasAnyField) {
            // Check if all fields are filled when at least one is filled
            if (!formik.values.MainAdminName || !formik.values.MainAdminSurname || 
                !formik.values.MainAdminEmail || !formik.values.MainAdminMobile) {
              alert("Please fill all fields for Main Administrator or leave all empty");
              return;
            }

            const isMainAdminEmailValid = await validateEmail(
              formik.values.MainAdminEmail,
              "MainAdminEmail"
            );
            if (!isMainAdminEmailValid) {
              return;
            }
          }
        }
      }

      // Create contact for current step
      if (currentStep === 1) {
        if (useExistingContact.SuperAdmin && selectedContactId.SuperAdmin) {
          setCreatedContactIds(prev => ({ ...prev, SuperAdmin: selectedContactId.SuperAdmin }));
        }
      } else if (currentStep === 2) {
        if (useExistingContact.ARCChief && selectedContactId.ARCChief) {
          setCreatedContactIds(prev => ({ ...prev, ARCChief: selectedContactId.ARCChief }));
        }
      } else if (currentStep === 3) {
        if (useExistingContact.MainAdmin && selectedContactId.MainAdmin) {
          setCreatedContactIds(prev => ({ ...prev, MainAdmin: selectedContactId.MainAdmin }));
        }
      }

    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } catch (validationErrors) {
      if (validationErrors?.inner) {
        const errors = {};
        validationErrors.inner.forEach(error => {
          errors[error.path] = error.message;
        });
        formik.setErrors(errors);
      }
    } finally {
      setIsAdvancingStep(false);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Handle skip step
  const handleSkip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStep === 2) {
      setUseExistingContact((prev) => ({ ...prev, ARCChief: false }));
      setSelectedContactId((prev) => ({ ...prev, ARCChief: null }));
      setCreatedContactIds((prev) => ({ ...prev, ARCChief: null }));
      formik.setFieldValue("ARCChiefName", "");
      formik.setFieldValue("ARCChiefSurname", "");
      formik.setFieldValue("ARCChiefEmail", "");
      formik.setFieldValue("ARCChiefMobile", "");
      setCurrentStep(3);
      return;
    }
    if (currentStep === 3) {
      setUseExistingContact((prev) => ({ ...prev, MainAdmin: false }));
      setSelectedContactId((prev) => ({ ...prev, MainAdmin: null }));
      setCreatedContactIds((prev) => ({ ...prev, MainAdmin: null }));
      formik.setFieldValue("MainAdminName", "");
      formik.setFieldValue("MainAdminSurname", "");
      formik.setFieldValue("MainAdminEmail", "");
      formik.setFieldValue("MainAdminMobile", "");
      setCurrentStep(4);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsOpen(false);
    setCurrentStep(1);
    formik.resetForm();
    setEmailErrors({
      SuperAdminEmail: "",
      ARCChiefEmail: "",
      MainAdminEmail: ""
    });
    setIsSubmitDisabled(false);
    setIsSubmitting(false);
    setIsAdvancingStep(false);
    setCreatedContactIds({
      SuperAdmin: null,
      ARCChief: null,
      MainAdmin: null
    });
    setUseExistingContact({
      SuperAdmin: false,
      ARCChief: false,
      MainAdmin: false
    });
    setSelectedContactId({
      SuperAdmin: null,
      ARCChief: null,
      MainAdmin: null
    });
  };

  // Check if all required fields are filled or an existing contact is selected
  const hasAllRequiredFieldsOrExisting = () => {
    if (currentStep === 2) {
      // If using existing contact, just check if one is selected
      if (useExistingContact.ARCChief) {
        return Boolean(selectedContactId.ARCChief);
      }
      // Otherwise check if all fields are filled
      const hasAnyField = formik.values.ARCChiefName || formik.values.ARCChiefSurname || 
                         formik.values.ARCChiefEmail || formik.values.ARCChiefMobile;
      if (!hasAnyField) return true; // Allow skip if no fields are filled
      return formik.values.ARCChiefName && formik.values.ARCChiefSurname && 
             formik.values.ARCChiefEmail && formik.values.ARCChiefMobile;
    }
    if (currentStep === 3) {
      // If using existing contact, just check if one is selected
      if (useExistingContact.MainAdmin) {
        return Boolean(selectedContactId.MainAdmin);
      }
      // Otherwise check if all fields are filled
      const hasAnyField = formik.values.MainAdminName || formik.values.MainAdminSurname || 
                         formik.values.MainAdminEmail || formik.values.MainAdminMobile;
      if (!hasAnyField) return true; // Allow skip if no fields are filled
      return formik.values.MainAdminName && formik.values.MainAdminSurname && 
             formik.values.MainAdminEmail && formik.values.MainAdminMobile;
    }
    return true; // For required steps, validation is handled by schema
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Step 1: Super Admin Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['SuperAdminName', 'SuperAdminSurname', 'SuperAdminMobile'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.replace(/([A-Z])/g, " $1").trim()}
                    {!useExistingContact.SuperAdmin && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={formik.values[field]}
                    onChange={formik.handleChange}
                    disabled={useExistingContact.SuperAdmin}
                    className={`w-full p-2 border border-gray-300 rounded mt-1 ${
                      useExistingContact.SuperAdmin ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                  {formik.touched[field] && formik.errors[field] && (
                    <div className="text-red-500 text-sm">{formik.errors[field]}</div>
                  )}
                </div>
              ))}
              
              {/* Super Admin Email with validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Super Admin Email
                  {!useExistingContact.SuperAdmin && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type="email"
                  name="SuperAdminEmail"
                  value={formik.values.SuperAdminEmail}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  disabled={useExistingContact.SuperAdmin}
                  className={`w-full p-2 border rounded mt-1 ${
                    useExistingContact.SuperAdmin ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                  } ${emailErrors.SuperAdminEmail ? 'border-red-500' : 'border-gray-300'}`}
                />
                {isEmailValidating.SuperAdminEmail && (
                  <div className="text-blue-500 text-sm">Validating email...</div>
                )}
                {emailErrors.SuperAdminEmail && (
                  <div className="text-red-500 text-sm">{emailErrors.SuperAdminEmail}</div>
                )}
                {formik.touched.SuperAdminEmail && formik.errors.SuperAdminEmail && (
                  <div className="text-red-500 text-sm">{formik.errors.SuperAdminEmail}</div>
                )}
              </div>
            </div>

            {/* Separator */}
            <hr className="my-6" />

            {/* Choose existing contact section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useExistingContact.SuperAdmin}
                  onChange={(e) => {
                    setUseExistingContact(prev => ({ ...prev, SuperAdmin: e.target.checked }));
                    if (e.target.checked) {
                      // Clear form fields when using existing contact
                      formik.setFieldValue('SuperAdminName', '');
                      formik.setFieldValue('SuperAdminSurname', '');
                      formik.setFieldValue('SuperAdminEmail', '');
                      formik.setFieldValue('SuperAdminMobile', '');
                    }
                  }}
                  className="w-4 h-4 text-[#90c740] rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Or choose a Contact</span>
              </label>

              {useExistingContact.SuperAdmin && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Contact
                    <span className="text-red-500"> *</span>
                  </label>
                  <ContactSearchDropdown
                    contacts={allContacts}
                    value={selectedContactId.SuperAdmin || ''}
                    onChange={(e) => setSelectedContactId(prev => ({ ...prev, SuperAdmin: e.target.value }))}
                    placeholder="-- Select a Contact --"
                    itemsPerPage={10}
                  />
                  {!selectedContactId.SuperAdmin && useExistingContact.SuperAdmin && (
                    <div className="text-red-500 text-sm mt-1">Please select a contact</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Step 2: ARC Chief Details (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['ARCChiefName', 'ARCChiefSurname', 'ARCChiefMobile'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.replace(/([A-Z])/g, " $1").trim()}
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={formik.values[field]}
                    onChange={formik.handleChange}
                    disabled={useExistingContact.ARCChief}
                    className={`w-full p-2 border border-gray-300 rounded mt-1 ${
                      useExistingContact.ARCChief ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                  {formik.touched[field] && formik.errors[field] && (
                    <div className="text-red-500 text-sm">{formik.errors[field]}</div>
                  )}
                </div>
              ))}
              
              {/* ARC Chief Email with validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  ARC Chief Email
                </label>
                <input
                  type="email"
                  name="ARCChiefEmail"
                  value={formik.values.ARCChiefEmail}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  disabled={useExistingContact.ARCChief}
                  className={`w-full p-2 border rounded mt-1 ${
                    useExistingContact.ARCChief ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                  } ${emailErrors.ARCChiefEmail ? 'border-red-500' : 'border-gray-300'}`}
                />
                {isEmailValidating.ARCChiefEmail && (
                  <div className="text-blue-500 text-sm">Validating email...</div>
                )}
                {emailErrors.ARCChiefEmail && (
                  <div className="text-red-500 text-sm">{emailErrors.ARCChiefEmail}</div>
                )}
                {formik.touched.ARCChiefEmail && formik.errors.ARCChiefEmail && (
                  <div className="text-red-500 text-sm">{formik.errors.ARCChiefEmail}</div>
                )}
              </div>
            </div>

            {/* Separator */}
            <hr className="my-6" />

            {/* Choose existing contact section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useExistingContact.ARCChief}
                  onChange={(e) => {
                    setUseExistingContact(prev => ({ ...prev, ARCChief: e.target.checked }));
                    if (e.target.checked) {
                      // Clear form fields when using existing contact
                      formik.setFieldValue('ARCChiefName', '');
                      formik.setFieldValue('ARCChiefSurname', '');
                      formik.setFieldValue('ARCChiefEmail', '');
                      formik.setFieldValue('ARCChiefMobile', '');
                    }
                  }}
                  className="w-4 h-4 text-[#90c740] rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Or choose a Contact</span>
              </label>

              {useExistingContact.ARCChief && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Contact
                    <span className="text-red-500"> *</span>
                  </label>
                  <ContactSearchDropdown
                    contacts={allContacts}
                    value={selectedContactId.ARCChief || ''}
                    onChange={(e) => setSelectedContactId(prev => ({ ...prev, ARCChief: e.target.value }))}
                    placeholder="-- Select a Contact --"
                    itemsPerPage={10}
                  />
                  {!selectedContactId.ARCChief && useExistingContact.ARCChief && (
                    <div className="text-red-500 text-sm mt-1">Please select a contact</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Step 3: Main Administrator Details (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['MainAdminName', 'MainAdminSurname', 'MainAdminMobile'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.replace(/([A-Z])/g, " $1").trim()}
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={formik.values[field]}
                    onChange={formik.handleChange}
                    disabled={useExistingContact.MainAdmin}
                    className={`w-full p-2 border border-gray-300 rounded mt-1 ${
                      useExistingContact.MainAdmin ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                  {formik.touched[field] && formik.errors[field] && (
                    <div className="text-red-500 text-sm">{formik.errors[field]}</div>
                  )}
                </div>
              ))}
              
              {/* Main Admin Email with validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Main Admin Email
                </label>
                <input
                  type="email"
                  name="MainAdminEmail"
                  value={formik.values.MainAdminEmail}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  disabled={useExistingContact.MainAdmin}
                  className={`w-full p-2 border rounded mt-1 ${
                    useExistingContact.MainAdmin ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                  } ${emailErrors.MainAdminEmail ? 'border-red-500' : 'border-gray-300'}`}
                />
                {isEmailValidating.MainAdminEmail && (
                  <div className="text-blue-500 text-sm">Validating email...</div>
                )}
                {emailErrors.MainAdminEmail && (
                  <div className="text-red-500 text-sm">{emailErrors.MainAdminEmail}</div>
                )}
                {formik.touched.MainAdminEmail && formik.errors.MainAdminEmail && (
                  <div className="text-red-500 text-sm">{formik.errors.MainAdminEmail}</div>
                )}
              </div>
            </div>

            {/* Separator */}
            <hr className="my-6" />

            {/* Choose existing contact section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useExistingContact.MainAdmin}
                  onChange={(e) => {
                    setUseExistingContact(prev => ({ ...prev, MainAdmin: e.target.checked }));
                    if (e.target.checked) {
                      // Clear form fields when using existing contact
                      formik.setFieldValue('MainAdminName', '');
                      formik.setFieldValue('MainAdminSurname', '');
                      formik.setFieldValue('MainAdminEmail', '');
                      formik.setFieldValue('MainAdminMobile', '');
                    }
                  }}
                  className="w-4 h-4 text-[#90c740] rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Or choose a Contact</span>
              </label>

              {useExistingContact.MainAdmin && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Contact
                    <span className="text-red-500"> *</span>
                  </label>
                  <ContactSearchDropdown
                    contacts={allContacts}
                    value={selectedContactId.MainAdmin || ''}
                    onChange={(e) => setSelectedContactId(prev => ({ ...prev, MainAdmin: e.target.value }))}
                    placeholder="-- Select a Contact --"
                    itemsPerPage={10}
                  />
                  {!selectedContactId.MainAdmin && useExistingContact.MainAdmin && (
                    <div className="text-red-500 text-sm mt-1">Please select a contact</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Step 4: Company Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["CompanyName", "CommercialName"].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700">
                    {field.replace(/([A-Z])/g, " $1").trim()}
                    <span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={formik.values[field]}
                    onChange={formik.handleChange}
                    className="w-full p-2 border border-gray-300 rounded mt-1"
                  />
                  {formik.touched[field] && formik.errors[field] && (
                    <div className="text-red-500 text-sm">{formik.errors[field]}</div>
                  )}
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Telephone 1
                  <span className="text-red-500"> *</span>
                </label>
                <input
                  type="text"
                  name="Telephone1"
                  value={formik.values.Telephone1}
                  onChange={formik.handleChange}
                  className="w-full p-2 border border-gray-300 rounded mt-1"
                />
                {formik.touched.Telephone1 && formik.errors.Telephone1 && (
                  <div className="text-red-500 text-sm">{formik.errors.Telephone1}</div>
                )}
              </div>

              <div>
                <CountryDropdown
                  value={formik.values.Country}
                  onChange={formik.handleChange}
                  countries={countries}
                />
                {formik.touched.Country && formik.errors.Country && (
                  <div className="text-red-500 text-sm">{formik.errors.Country}</div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Invoice Address
                  <span className="text-red-500"> *</span>
                </label>
                <input
                  type="text"
                  name="InvoiceAddress"
                  value={formik.values.InvoiceAddress}
                  onChange={formik.handleChange}
                  className="w-full p-2 border border-gray-300 rounded mt-1"
                />
                {formik.touched.InvoiceAddress && formik.errors.InvoiceAddress && (
                  <div className="text-red-500 text-sm">{formik.errors.InvoiceAddress}</div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="btn-create-customer w-full sm:w-auto"
      >
        Create Customer
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-3 sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div 
            ref={modalRef} 
            className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-none flex justify-between items-start gap-3 mb-6">
              <div>
                <h2 className="text-xl font-bold">Create Customer</h2>
                <p className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</p>
              </div>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-red-500 text-2xl">✖</button>
            </div>

            {/* Progress Bar */}
            <div className="flex-none mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm text-gray-500">{Math.round((currentStep / totalSteps) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#90c740] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Step Content */}
            <form
              onSubmit={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex-1 overflow-y-auto min-h-0 pr-1">
                {renderStepContent()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex-none flex flex-col gap-3 mt-8 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      disabled={false}
                      className="w-full px-4 py-2 rounded sm:w-auto bg-gray-300 text-gray-700 hover:bg-gray-400"
                    >
                      Previous
                    </button>
                  )}
                </div>
                
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  {currentStep < totalSteps ? (
                    <>
                      {currentStep === 2 || currentStep === 3 ? (
                        <button
                          type="button"
                          onClick={handleSkip}
                          className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 sm:w-auto"
                        >
                          Skip
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={Boolean(
                          isAdvancingStep ||
                          (currentStep === 1 && (
                            (!useExistingContact.SuperAdmin && (
                              !formik.values.SuperAdminName ||
                              !formik.values.SuperAdminSurname ||
                              !formik.values.SuperAdminEmail ||
                              !formik.values.SuperAdminMobile ||
                              emailErrors.SuperAdminEmail ||
                              formik.errors.SuperAdminName ||
                              formik.errors.SuperAdminSurname ||
                              formik.errors.SuperAdminEmail ||
                              formik.errors.SuperAdminMobile
                            )) ||
                            (useExistingContact.SuperAdmin && !selectedContactId.SuperAdmin)
                          )) ||
                          ((currentStep === 2 || currentStep === 3) && !hasAllRequiredFieldsOrExisting())
                        )}
                        className={`w-full px-4 py-2 text-white rounded sm:w-auto flex items-center justify-center gap-2 ${
                          isAdvancingStep ||
                          (currentStep === 1 && (
                            (!useExistingContact.SuperAdmin && (
                              !formik.values.SuperAdminName ||
                              !formik.values.SuperAdminSurname ||
                              !formik.values.SuperAdminEmail ||
                              !formik.values.SuperAdminMobile ||
                              emailErrors.SuperAdminEmail ||
                              formik.errors.SuperAdminName ||
                              formik.errors.SuperAdminSurname ||
                              formik.errors.SuperAdminEmail ||
                              formik.errors.SuperAdminMobile
                            )) ||
                            (useExistingContact.SuperAdmin && !selectedContactId.SuperAdmin)
                          )) ||
                          (currentStep === 2 && !hasAllRequiredFieldsOrExisting()) ||
                          (currentStep === 3 && !hasAllRequiredFieldsOrExisting())
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-[#90c740] hover:bg-[#7ebd2e]'
                        }`}
                      >
                        {isAdvancingStep ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Please wait...
                          </>
                        ) : (
                          "Next"
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCreateCustomer}
                      disabled={!isCreateArmed || isSubmitDisabled || isSubmitting}
                      className={`w-full px-4 py-2 text-white rounded sm:w-auto ${
                        !isCreateArmed || isSubmitDisabled || isSubmitting
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-[#90c740] hover:bg-[#7ebd2e]'
                      }`}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Customer'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCustomer;
